import { useState } from 'react';
import { useFeedback } from '../common/Feedback';
import { Modal } from '../common/Modal';
import { X, Download, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useCredentialImport, ImportReport } from '../../hooks/useCredentialImport';
import { SeletorDeArquivo } from '../common/SeletorDeArquivo';

interface ImportModalProps {
  onClose: () => void;
  onImported: () => Promise<void> | void;
}

type Step = 'choose' | 'preview' | 'done';

const buttonPrimary = (disabled: boolean): React.CSSProperties => ({
  padding: 'var(--space-3) var(--space-4)',
  background: 'var(--color-primary-600)',
  color: 'var(--color-primary-50)',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontWeight: 'var(--font-medium)',
  opacity: disabled ? 0.6 : 1,
});

const buttonSecondary: React.CSSProperties = {
  padding: 'var(--space-3) var(--space-4)',
  background: 'transparent',
  border: '1px solid var(--color-border-default)',
  color: 'var(--color-text-secondary)',
  borderRadius: 'var(--radius-md)',
  cursor: 'pointer',
  fontWeight: 'var(--font-medium)',
};

const warningBox: React.CSSProperties = {
  padding: 'var(--space-3)',
  background: 'rgba(245, 158, 11, 0.08)',
  border: '1px solid rgba(245, 158, 11, 0.25)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--text-xs)',
  color: 'var(--color-text-secondary)',
  lineHeight: 1.5,
};

const cell: React.CSSProperties = {
  padding: 'var(--space-2)',
  borderBottom: '1px solid var(--color-border-default)',
  verticalAlign: 'top',
  textAlign: 'left',
};

export function ImportModal({ onClose, onImported }: ImportModalProps) {
  const { confirmar } = useFeedback();
  const { downloadTemplate, importFile } = useCredentialImport();
  const [step, setStep] = useState<Step>('choose');
  const [file, setFile] = useState<File | null>(null);
  const [report, setReport] = useState<ImportReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  const run = async (action: () => Promise<void>) => {
    setIsBusy(true);
    setError(null);
    try {
      await action();
    } catch (err: any) {
      setError(err.message || 'Erro ao processar a planilha.');
    } finally {
      setIsBusy(false);
    }
  };

  const handleDownload = () => run(downloadTemplate);

  const handlePreview = () => run(async () => {
    if (!file) return;
    setReport(await importFile(file, true));
    setStep('preview');
  });

  const handleConfirm = () => run(async () => {
    if (!file || !report) return;
    if (!(await confirmar({ titulo: `Criar ${report.valid_rows} credencial(is)?`, mensagem: 'As linhas válidas da planilha viram credenciais novas no cofre.', confirmar: 'Importar' }))) return;
    const result = await importFile(file, false);
    setReport(result);
    if (result.created > 0) {
      setStep('done');
      await onImported();
    }
  });

  const handleChooseAnother = () => {
    setFile(null);
    setReport(null);
    setError(null);
    setStep('choose');
  };

  return (
    <Modal pedirParaFechar={onClose}>
      <div style={{
        background: 'var(--color-bg-surface)',
        border: '1px solid var(--color-border-default)',
        borderRadius: 'var(--radius-xl)',
        width: '100%',
        maxWidth: step === 'preview' ? '900px' : '520px',
        padding: 'var(--space-8)',
        maxHeight: '90vh',
        overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-6)' }}>
          <h2 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-semibold)', color: 'var(--color-text-primary)' }}>
            Importar credenciais por planilha
          </h2>
          <button type="button" onClick={onClose} aria-label="Fechar" title="Fechar" className="botao-icone botao-icone--sem-borda">
            <X size={24} />
          </button>
        </div>

        {error && (
          <div style={{ padding: 'var(--space-3)', background: 'var(--color-error-muted)', color: 'var(--color-error)', borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-4)', fontSize: 'var(--text-sm)' }}>
            {error}
          </div>
        )}

        {step === 'choose' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
              Baixe o modelo, preencha uma linha por credencial (a aba "Como preencher" explica cada coluna) e envie o arquivo aqui.
              Antes de gravar, mostramos uma prévia linha a linha. Se alguma linha tiver erro, nada é gravado.
            </p>
            <button type="button" onClick={handleDownload} disabled={isBusy} style={{ ...buttonSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-2)' }}>
              <Download size={16} /> Baixar modelo (.xlsx)
            </button>
            <SeletorDeArquivo
              id="import-file"
              accept=".xlsx,.csv"
              dica=".xlsx ou .csv"
              arquivo={file}
              onChange={setFile}
              disabled={isBusy}
            />
            <div style={{ display: 'flex', gap: 'var(--space-4)', marginTop: 'var(--space-2)' }}>
              <button type="button" onClick={onClose} style={{ ...buttonSecondary, flex: 1 }}>Cancelar</button>
              <button type="button" onClick={handlePreview} disabled={!file || isBusy} style={{ ...buttonPrimary(!file || isBusy), flex: 1 }}>
                {isBusy ? 'Lendo planilha...' : 'Ver prévia'}
              </button>
            </div>
          </div>
        )}

        {step === 'preview' && report && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-primary)' }}>
              {report.total_rows} linha(s): <strong>{report.valid_rows} pronta(s)</strong>
              {report.error_rows > 0 && <>, <strong style={{ color: 'var(--color-error)' }}>{report.error_rows} com erro</strong></>}.
            </p>
            {report.error_rows > 0 && (
              <div style={{ padding: 'var(--space-3)', background: 'var(--color-error-muted)', color: 'var(--color-error)', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)' }}>
                Corrija as linhas com erro na planilha e envie o arquivo de novo. Nada foi gravado.
              </div>
            )}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-xs)', color: 'var(--color-text-primary)' }}>
                <thead>
                  <tr style={{ color: 'var(--color-text-secondary)' }}>
                    <th style={cell}>Linha</th>
                    <th style={cell}>Credencial</th>
                    <th style={cell}>Grupos</th>
                    <th style={cell}>Titular</th>
                    <th style={cell}>Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {report.rows.map(row => (
                    <tr key={row.row}>
                      <td style={cell}>{row.row}</td>
                      <td style={cell}>
                        <div>{row.label || '—'}</div>
                        <div style={{ color: 'var(--color-text-secondary)' }}>{row.court_label || row.url || ''}</div>
                      </td>
                      <td style={cell}>{row.groups.join(', ') || '—'}</td>
                      <td style={cell}>{row.holder ? `${row.holder}${row.holder_new ? ' (novo)' : ''}` : '—'}</td>
                      <td style={cell}>
                        {row.status === 'ok'
                          ? <span style={{ color: 'var(--color-primary-400)' }}>OK</span>
                          : row.errors.map((e, i) => (
                            <div key={i} style={{ color: 'var(--color-error)' }}>coluna {e.column}: {e.message}</div>
                          ))}
                        {row.warnings.map((w, i) => (
                          <div key={i} style={{ color: '#f59e0b' }}>Aviso: {w}</div>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', gap: 'var(--space-4)', marginTop: 'var(--space-2)' }}>
              <button type="button" onClick={handleChooseAnother} style={{ ...buttonSecondary, flex: 1 }}>Escolher outro arquivo</button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={report.error_rows > 0 || report.valid_rows === 0 || isBusy}
                style={{ ...buttonPrimary(report.error_rows > 0 || report.valid_rows === 0 || isBusy), flex: 1 }}
              >
                {isBusy ? 'Importando...' : `Importar ${report.valid_rows} credencial(is)`}
              </button>
            </div>
          </div>
        )}

        {step === 'done' && report && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <p style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--text-sm)', color: 'var(--color-text-primary)' }}>
              <CheckCircle2 size={18} style={{ color: 'var(--color-primary-400)' }} />
              {report.created} credencial(is) importada(s) com sucesso.
            </p>
            <div style={{ ...warningBox, display: 'flex', gap: 'var(--space-2)' }}>
              <AlertTriangle size={16} style={{ color: '#f59e0b', flexShrink: 0 }} />
              <span><strong>Apague a planilha do seu computador</strong> (e da lixeira): ela contém senhas.</span>
            </div>
            <button type="button" onClick={onClose} style={buttonPrimary(false)}>Fechar</button>
          </div>
        )}
      </div>
    </Modal>
  );
}
